import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServices } from '@/shared/hooks/use-services';
import type { CheckPendingOrdersResponse } from '../types/pending-orders';
import { useAuth } from '@/modules/auth/hooks/use-auth';
import { PendingOrdersContext, POLLING_INTERVAL } from './pending-orders-context-types';

interface PendingOrdersProviderProps {
  children: ReactNode;
}

export function PendingOrdersProvider({ children }: PendingOrdersProviderProps) {
  const { checkPendingOrders } = useServices();
  const { isAuthenticated } = useAuth();
  
  // Log para verificar o estado de autenticação
  console.log('[PendingOrders] Estado de autenticação:', { isAuthenticated });

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<CheckPendingOrdersResponse, Error>({
    queryKey: ['pending-orders'],
    queryFn: async () => {
      console.log('[PendingOrders] Iniciando requisição de pedidos pendentes');
      try {
        const result = await checkPendingOrders.execute();
        console.log('[PendingOrders] Requisição bem-sucedida:', result);
        return result;
      } catch (err) {
        console.error('[PendingOrders] Erro na requisição:', err);
        throw err;
      }
    },
    enabled: isAuthenticated, // Só executa se o usuário estiver autenticado
    refetchInterval: isAuthenticated ? POLLING_INTERVAL : false, // Só faz polling se autenticado
    refetchIntervalInBackground: true,
    staleTime: 0, // Sempre considerar os dados obsoletos para forçar refetch
    gcTime: POLLING_INTERVAL, // Tempo de cache igual ao intervalo de polling
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnMount: isAuthenticated, // Só refetch ao montar se autenticado
    refetchOnWindowFocus: isAuthenticated, // Só refetch no foco se autenticado
  });

  // Forçar a primeira requisição imediatamente após a montagem do componente ou quando o usuário se autenticar
  useEffect(() => {
    // Só tenta refetch se o usuário estiver autenticado
    if (isAuthenticated) {
      console.log('[PendingOrders] Forçando primeira requisição...');
      // Pequeno timeout para garantir que o estado de autenticação foi processado pelo React Query
      setTimeout(() => {
        refetch().then(() => {
          console.log('[PendingOrders] Primeira requisição concluída');
        }).catch(err => {
          console.error('[PendingOrders] Erro na primeira requisição:', err);
        });
      }, 100);
    } else {
      console.log('[PendingOrders] Usuário não autenticado, requisição não será feita');
    }
  }, [isAuthenticated, refetch]);

  // Log de estado quando os dados mudam
  useEffect(() => {
    console.log('[PendingOrders] Estado atual:', {
      hasPendingOrders: data?.has_pending_orders,
      pendingOrdersCount: data?.pending_orders?.length || 0,
      isLoading,
      hasError: !!error
    });
  }, [data, isLoading, error]);

  const value = {
    hasPendingOrders: data?.has_pending_orders || false,
    pendingOrders: data?.pending_orders || [],
    isLoading,
    error,
    refetch,
  };

  return (
    <PendingOrdersContext.Provider value={value}>
      {children}
    </PendingOrdersContext.Provider>
  );
}
